import { useContext, useEffect } from "react";
import { API_Version, ApiVersionInfo, ResultObj, allFetch, getControllerApiVersion } from "../Domain";
import { ApiContext, useApi } from "../apiProvider";
import { DDMenu } from "../api";
import { CityDDMenuItem } from "../types";


// SystemParamApi的Hooks使用方式
export const useSystemParamApi = () => {
    const apiManagerName = "systemParamApiManager";
    const context = useContext(ApiContext);
    if (!context) {
        throw new Error("useSystemParamApi must be used within a ApiProvider");
    }

    let { apiManagers, registerApiManager } = context;
    useEffect(() => {
        if (!apiManagers[apiManagerName]) {
            const newSystemParamApiManager = new SystemParamApiManager();
            newSystemParamApiManager.init().then((apiManager) => {
                registerApiManager(apiManagerName, apiManager);
            }).catch((e) => {
                console.error("Error initializing API Manager", e);
            });
        }
    }, [apiManagers[apiManagerName]]);

    return useApi<SystemParamApiManager>(apiManagerName);
};

// SystemParamApiInterface 的定義
export interface SystemParamApiInterface {
    PrimaryList: () => Promise<ResultObj<PrimaryListResItem[]>>;
    AddPrimary: (obj: AddPrimaryParams) => Promise<ResultObj>;
    PrimaryDetail: (obj: PrimaryDetailParams) => Promise<ResultObj<PrimaryDetailtResItem>>;
    UpdatePrimary: (obj: UpdatePrimaryParams) => Promise<ResultObj>;
    SecondList: (obj: SecondListParams) => Promise<ResultObj<SecondListResItem[]>>;
    AddSecond: (obj: AddSecondParams) => Promise<ResultObj>;
    SecondDetail: (obj: SecondDetailParams) => Promise<ResultObj>;
    UpdateSecond: (obj: UpdateSecondParams) => Promise<ResultObj>;
    DeleteSecond: (obj: DeleteSecondParams) => Promise<ResultObj>;
    CityAreaSearch: () => Promise<ResultObj<CityAreaSearchResItem[]>>;
}

// SystemParamApiManager 類別實現
export class SystemParamApiManager implements SystemParamApiInterface {

    private adapter: SystemParamDotnet6Aadpter | SystemParamFrameworkAadpter | null = null;
    private version: keyof typeof API_Version | undefined;

    constructor(version?: keyof typeof API_Version) {
        this.version = version;
    }

    async init(): Promise<SystemParamApiManager> {
        let target_version: string | undefined = this.version;
        if (target_version === undefined) {
            let getAPIversionResult: ApiVersionInfo | null = await getControllerApiVersion("SystemParam");
            target_version = getAPIversionResult === null ? this.version : getAPIversionResult.version;
        }

        if (target_version === "dotNet6") this.adapter = new SystemParamDotnet6Aadpter();
        else if (target_version === 'dotNet6_AM_N') this.adapter = new SystemParamDotnet6Aadpter();//預設是 dotNet6，有需要才再另寫Adapter
        else if (target_version === "framework") this.adapter = new SystemParamFrameworkAadpter();

        if (this.adapter !== null) {
            this.PrimaryList = this.adapter.PrimaryList;
            this.AddPrimary = this.adapter.AddPrimary;
            this.PrimaryDetail = this.adapter.PrimaryDetail;
            this.UpdatePrimary = this.adapter.UpdatePrimary;
            this.SecondList = this.adapter.SecondList;
            this.AddSecond = this.adapter.AddSecond;
            this.SecondDetail = this.adapter.SecondDetail;
            this.UpdateSecond = this.adapter.UpdateSecond;
            this.DeleteSecond = this.adapter.DeleteSecond;
            this.CityAreaSearch = this.adapter.CityAreaSearch;
        }
        return this;
    }

    emptyAdapter = async (): Promise<ResultObj> => ({ success: false, message: 'API未實現[SystemParamAdapter]', data: null });

    PrimaryList = async (): Promise<ResultObj<PrimaryListResItem[]>> => await this.emptyAdapter();
    AddPrimary = async (obj: AddPrimaryParams): Promise<ResultObj> => await this.emptyAdapter();
    PrimaryDetail = async (obj: PrimaryDetailParams): Promise<ResultObj<PrimaryDetailtResItem>> => await this.emptyAdapter();
    UpdatePrimary = async (obj: UpdatePrimaryParams): Promise<ResultObj> => await this.emptyAdapter();
    SecondList = async (obj: SecondListParams): Promise<ResultObj<SecondListResItem[]>> => await this.emptyAdapter();
    AddSecond = async (obj: AddSecondParams): Promise<ResultObj> => await this.emptyAdapter();
    SecondDetail = async (obj: SecondDetailParams): Promise<ResultObj> => await this.emptyAdapter();
    UpdateSecond = async (obj: UpdateSecondParams): Promise<ResultObj> => await this.emptyAdapter();
    DeleteSecond = async (obj: DeleteSecondParams): Promise<ResultObj> => await this.emptyAdapter();
    CityAreaSearch = async (): Promise<ResultObj<CityAreaSearchResItem[]>> => await this.emptyAdapter();
}

class SystemParamDotnet6Aadpter implements SystemParamApiInterface {
    PrimaryList = async (): Promise<ResultObj<PrimaryListResItem[]>> => await allFetch('SystemParam/PrimaryList', {});
    AddPrimary = async (obj: AddPrimaryParams): Promise<ResultObj> => await allFetch('SystemParam/AddPrimary', obj);
    PrimaryDetail = async (obj: PrimaryDetailParams): Promise<ResultObj<PrimaryDetailtResItem>> => await allFetch('SystemParam/PrimaryDetail', obj);
    UpdatePrimary = async (obj: UpdatePrimaryParams): Promise<ResultObj> => await allFetch('SystemParam/UpdatePrimary', obj);
    SecondList = async (obj: SecondListParams): Promise<ResultObj<SecondListResItem[]>> => await allFetch('SystemParam/SecondList', obj);
    AddSecond = async (obj: AddSecondParams): Promise<ResultObj> => await allFetch('SystemParam/AddSecond', obj);
    SecondDetail = async (obj: SecondDetailParams): Promise<ResultObj> => await allFetch('SystemParam/SecondDetail', obj);
    UpdateSecond = async (obj: UpdateSecondParams): Promise<ResultObj> => await allFetch('SystemParam/UpdateSecond', obj);
    DeleteSecond = async (obj: DeleteSecondParams): Promise<ResultObj> => await allFetch('SystemParam/DeleteSecond', obj);
    CityAreaSearch = async (): Promise<ResultObj<CityAreaSearchResItem[]>> => await allFetch('SystemParam/CityAreaSearch');
}

class SystemParamFrameworkAadpter implements SystemParamApiInterface {
    PrimaryList = async (): Promise<ResultObj<PrimaryListResItem[]>> => await allFetch('SystemParam/PrimaryList', {});
    AddPrimary = async (obj: AddPrimaryParams): Promise<ResultObj> => await allFetch('SystemParam/AddPrimary', obj);
    PrimaryDetail = async (obj: PrimaryDetailParams): Promise<ResultObj<PrimaryDetailtResItem>> =>
        await allFetch('SystemParam/PrimaryDetail', obj).then((res: ResultObj) => {
            return ({
                success: res.success,
                message: res.message,
                data: Object.getPrototypeOf(res.data).constructor.name === "Array" && res.data.length > 0
                    ? res.data[0]
                    : res.data
            })
        });

    UpdatePrimary = async (obj: UpdatePrimaryParams): Promise<ResultObj> => await allFetch('SystemParam/UpdatePrimary', obj);
    SecondList = async (obj: SecondListParams): Promise<ResultObj<SecondListResItem[]>> => await allFetch('SystemParam/SecondList', obj);
    AddSecond = async (obj: AddSecondParams): Promise<ResultObj> => await allFetch('SystemParam/AddSecond', obj);
    SecondDetail = async (obj: SecondDetailParams): Promise<ResultObj> => await allFetch('SystemParam/SecondDetail', obj);
    UpdateSecond = async (obj: UpdateSecondParams): Promise<ResultObj> => await allFetch('SystemParam/UpdateSecond', obj);
    DeleteSecond = async (obj: DeleteSecondParams): Promise<ResultObj> => await allFetch('SystemParam/DeleteSecond', obj);
    CityAreaSearch = async (): Promise<ResultObj<CityAreaSearchResItem[]>> => await DDMenu.nestcode<CityAreaSearchResItem>({ menuName: 'cityareazip' }).then(res => {
        return ({
            success: res.length ? true : false,
            message: res.length ? '查詢成功' : '查詢代碼檔失敗',
            data: res
        })
    });
}

//大分類查詢 (回傳)
export type PrimaryListResItem = {
    name: string;
    remark: string;
    spp_id: string;
    su: "Y" | "N";
}

// API 呼叫參數類型定義
export type AddPrimaryParams = {
    spp_id: string;
    su: "Y" | "N";
    name: string;
    remark?: string;
};

export type PrimaryDetailParams = {
    spp_id: string;
};

//大分類查詢 (回傳)
export type PrimaryDetailtResItem = {
    name: string;
    remark: string;
    spp_id: string;
    su: "Y" | "N";
}

export type UpdatePrimaryParams = AddPrimaryParams;

export type SecondListParams = {
    spp_id: string;
    visible?: "Y" | "N";
};

export type SecondListResItem = {
    code: string;
    name: string;
    remark: string;
    seq: number;
    spp_id: string;
    sps_id: string;
    visible: "Y" | "N"
}

export type AddSecondParams = {
    spp_id: string;
    code?: string;
    name: string;
    remark?: string;
    seq: number;
};

export type SecondDetailParams = {
    spp_id: string;
    sps_id: string;
};

export type UpdateSecondParams = AddSecondParams & {
    sps_id: string;
    visible: "Y" | "N";
};

export type DeleteSecondParams = SecondDetailParams;

// ... 使用 ApiContext 和 useApi 鉤子 ...

/**@description 查詢城市地區選單回傳格式 */
export type CityAreaSearchResItem = {
    name: string,
    children: {
        name: string,
        code: string
    }[]
}